import { Button, Popover, PopoverContent, PopoverTrigger, RadioGroup, Skeleton } from '@bpinternal/ui-kit-next'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import { BotAnalyticsMetric, BotAnalyticsMetricKeys } from '~/features/bots/types'
import { cn } from '~/utils'
import { useAgentAnalyticsContext } from './AgentAnalyticsContext'
import { AnalyticsOverview, AnalyticsOverviewSkeleton } from './AnalyticsOverview'

type AnalyticsMetricSelectorProps = {
  botId: string
  workspaceId: string
}

export const AnalyticsMetricSelectorSkeleton = () => (
  <>
    <div className="flex w-full items-center justify-between md:hidden">
      <h3 className="text-base font-medium">Activity</h3>
      <Skeleton className="h-8 w-48" />
    </div>
    <div className="hidden grid-cols-3 gap-0 divide-x divide-border md:grid">
      {BotAnalyticsMetricKeys.map((metric) => (
        <AnalyticsOverviewSkeleton key={metric} />
      ))}
    </div>
  </>
)

export const AnalyticsMetricSelector = ({ botId, workspaceId }: AnalyticsMetricSelectorProps) => {
  const [open, setOpen] = useState(false)
  const { selectedMetric, setSelectedMetric, getLabel } = useAgentAnalyticsContext()

  const metricOptions = BotAnalyticsMetricKeys.map((metric) => ({
    label: getLabel(metric),
    value: metric,
  }))

  const AnalyticsMobileSelector = () => (
    <div className="flex w-full items-center justify-between md:hidden">
      <h3 className="text-base font-medium">Activity</h3>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn('w-48 justify-between', open && 'bg-neutral-50 ring-1 ring-foreground')}
          >
            {getLabel(selectedMetric)}
            <ChevronsUpDown />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 gap-0 p-0" asChild>
          <RadioGroup
            value={selectedMetric}
            onValueChange={(value) => {
              setSelectedMetric(value as BotAnalyticsMetric)
              setOpen(false)
            }}
            className="m-0 divide-y divide-border"
          >
            {metricOptions.map((option) => (
              <RadioGroupPrimitive.Item
                key={option.value}
                value={option.value}
                className="p-2 text-start text-sm hover:bg-neutral-50 data-[state=checked]:bg-neutral-100"
              >
                {option.label}
              </RadioGroupPrimitive.Item>
            ))}
          </RadioGroup>
        </PopoverContent>
      </Popover>
    </div>
  )

  return (
    <>
      <AnalyticsMobileSelector />
      <RadioGroup
        value={selectedMetric}
        onValueChange={setSelectedMetric}
        className="hidden grid-cols-3 gap-0 divide-x divide-border md:grid"
      >
        {BotAnalyticsMetricKeys.map((metric) => (
          <RadioGroupPrimitive.Item
            key={metric}
            value={metric}
            className="transition-colors duration-300 ease-out hover:bg-neutral-50 data-[state=checked]:bg-neutral-100"
          >
            <AnalyticsOverview key={metric} botId={botId} workspaceId={workspaceId} metric={metric} />
          </RadioGroupPrimitive.Item>
        ))}
      </RadioGroup>
    </>
  )
}
